import React, { useRef, useState } from "react";
import { api, formatApiError } from "@/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { CheckCircle2, Loader2, Paperclip, Send } from "lucide-react";
import { FieldInput } from "./RecordDialog";

const Lbl = ({ label, required, help, children }) => (
  <label className="block space-y-1"><span className="text-[10px] uppercase tracking-[0.2em] font-semibold opacity-70">{label}{required && <span className="text-[#E60000]"> *</span>}</span>{children}{help && <span className="block text-[11px] opacity-60">{help}</span>}</label>
);

/**
 * Phase 90 — Formulaire public d'un module (volet client), partagé par le site Pro, le mini-site et l'espace client.
 * mode "public" : nom + email + téléphone demandés, lien de suivi envoyé par email ; mode "portal" : client connu (token), pièces jointes possibles après création.
 */
export function ClientRequestForm({ form, submitUrl, uploadUrl, mode = "public", successMessage, buttonLabel, onDone, dark = false, testid = "crf" }) {
  const [data, setData] = useState(() => Object.fromEntries(form.fields.map((f) => [f.key, f.default ?? (f.type === "bool" ? false : null)])));
  const [who, setWho] = useState({ name: "", email: "", phone: "", website: "" });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(null);
  const [uploading, setUploading] = useState("");
  const fileRefs = useRef({});
  const inputCls = dark ? "bg-white/10 border-white/20 text-white placeholder:text-white/40" : "";

  const submit = async (e) => {
    e.preventDefault();
    const missing = form.fields.filter((f) => f.required && (data[f.key] == null || data[f.key] === "" || (Array.isArray(data[f.key]) && !data[f.key].length))).map((f) => f.label);
    if (mode === "public" && (!who.name.trim() || !who.email.trim())) missing.unshift("Nom", "Email");
    if (missing.length) { setError(`Champs obligatoires : ${[...new Set(missing)].join(", ")}`); return; }
    setBusy(true); setError("");
    try {
      const body = mode === "public" ? { ...who, data } : { data };
      const { data: res } = await api.post(submitUrl, body);
      setDone(res);
      onDone?.(res);
    } catch (err) { const msg = formatApiError(err.response?.data?.detail) || "Envoi impossible"; setError(msg); toast.error(msg); }
    finally { setBusy(false); }
  };
  const upload = async (field, file) => {
    if (!file || !done?.id) return;
    setUploading(field.key);
    const fd = new FormData();
    fd.append("file", file);
    try { const { data: att } = await api.post(`${uploadUrl(done.id)}?field=${field.key}`, fd, { headers: { "Content-Type": "multipart/form-data" } }); setDone((d) => ({ ...d, attachments: [...(d.attachments || []), att] })); toast.success("Pièce jointe ajoutée"); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Envoi impossible"); }
    finally { setUploading(""); }
  };

  if (done) {
    return (
      <div className={`border p-6 ${dark ? "border-white/20 bg-white/5" : "border-[#A7F3D0] bg-[#ECFDF5]"}`} data-testid={`${testid}-success`}>
        <div className="flex items-start gap-3">
          <CheckCircle2 className="h-6 w-6 text-[#059669] shrink-0" />
          <div className="flex-1">
            <div className="font-display font-black text-lg">{mode === "public" ? "Demande envoyée !" : "Demande enregistrée"}</div>
            <p className="text-sm mt-1 opacity-80" data-testid={`${testid}-success-message`}>{successMessage || done.message || "Votre demande a bien été prise en compte."}</p>
            {done.title && <div className="text-xs mt-2 opacity-60">Référence : <b>{done.title}</b></div>}
            {mode === "portal" && form.file_fields?.length > 0 && (
              <div className="mt-4 space-y-2" data-testid={`${testid}-uploads`}>
                <div className="text-[10px] uppercase tracking-[0.2em] font-semibold opacity-70">Ajouter des pièces jointes (facultatif)</div>
                {form.file_fields.map((ff) => (
                  <div key={ff.key} className="flex items-center gap-2 text-xs">
                    <input ref={(el) => { fileRefs.current[ff.key] = el; }} type="file" className="hidden" onChange={(e) => upload(ff, e.target.files?.[0])} data-testid={`${testid}-file-${ff.key}`} />
                    <Button type="button" size="sm" variant="outline" disabled={uploading === ff.key} onClick={() => fileRefs.current[ff.key]?.click()} className={`h-8 text-xs ${dark ? "border-white/30 text-white hover:bg-white hover:text-black" : ""}`} data-testid={`${testid}-file-add-${ff.key}`}>{uploading === ff.key ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <Paperclip className="h-3 w-3 mr-1" />}{ff.label}</Button>
                    <span className="opacity-60">{(done.attachments || []).filter((a) => a.field === ff.key).map((a) => a.name).join(", ")}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="space-y-4" data-testid={`${testid}-form`} noValidate>
      {form.intro && <p className="text-sm opacity-80" data-testid={`${testid}-intro`}>{form.intro}</p>}
      {mode === "public" && (
        <div className="grid sm:grid-cols-3 gap-3" data-testid={`${testid}-identity`}>
          <Lbl label="Votre nom" required><Input value={who.name} onChange={(e) => setWho({ ...who, name: e.target.value })} className={`h-10 ${inputCls}`} autoComplete="name" data-testid={`${testid}-name`} /></Lbl>
          <Lbl label="Email" required help="Vous recevrez votre lien de suivi personnel"><Input type="email" value={who.email} onChange={(e) => setWho({ ...who, email: e.target.value })} className={`h-10 ${inputCls}`} autoComplete="email" data-testid={`${testid}-email`} /></Lbl>
          <Lbl label="Téléphone"><Input type="tel" value={who.phone} onChange={(e) => setWho({ ...who, phone: e.target.value })} className={`h-10 ${inputCls}`} autoComplete="tel" data-testid={`${testid}-phone`} /></Lbl>
          <input type="text" value={who.website} onChange={(e) => setWho({ ...who, website: e.target.value })} className="hidden" tabIndex={-1} autoComplete="off" aria-hidden="true" data-testid={`${testid}-hp`} />
        </div>
      )}
      <div className="grid sm:grid-cols-2 gap-3">
        {form.fields.map((f) => (
          <div key={f.key} className={f.type === "textarea" || f.type === "multiselect" ? "sm:col-span-2" : ""}>
            <Lbl label={f.label} required={f.required} help={f.help}><FieldInput f={f} value={data[f.key]} onChange={(v) => setData((d) => ({ ...d, [f.key]: v }))} refs={{}} relations={{}} /></Lbl>
          </div>
        ))}
      </div>
      {form.file_fields?.length > 0 && mode === "public" && <div className="text-[11px] opacity-60">Pièces jointes ({form.file_fields.map((f) => f.label).join(", ")}) : vous pourrez les ajouter depuis votre espace client après l'envoi.</div>}
      {error && <div className="border-l-4 border-[#E60000] bg-[#FEF2F2] px-3 py-2 text-xs text-[#991B1B]" role="alert" data-testid={`${testid}-error`}>{error}</div>}
      <Button type="submit" disabled={busy} className="h-11 bg-[#002FA7] hover:bg-[#00247F] text-white font-bold px-6" data-testid={`${testid}-submit`}>{busy ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Send className="h-4 w-4 mr-2" />}{buttonLabel || form.button_label || "Envoyer ma demande"}</Button>
    </form>
  );
}
